import type { ReactNode } from 'react';
import Animated from 'react-native-reanimated';

import type { MOTION } from '@/constants/motion';
import { useEntranceAnimation } from '@/hooks/useEntranceAnimation';

export interface PhaseEnterProps {
  preset: typeof MOTION.entrance.subtle | typeof MOTION.entrance.standard;
  children: ReactNode;
  /** Stretches the wrapper to fill the parent's remaining cross-axis
   * height and allows it to shrink below content size. Required for
   * phases whose body is a flex-1 ScrollView (the review phase) — without
   * it, `Animated.View` defaults to `flex-shrink: 0` on
   * react-native-web, so the wrapper expands to its natural content
   * height, escapes the modal's `max-h-[90vh] overflow-hidden` cap, and
   * the inner scrollview never sees a bounded height to scroll within. */
  fill?: boolean;
}

/**
 * Thin entrance-animation wrapper. Each phase body is rendered inside
 * one of these, and because phases are conditionally mounted (one at a
 * time) the wrapper mounts fresh whenever the wizard switches phase —
 * which fires `useEntranceAnimation` exactly once per phase entry.
 * Result: every phase swap fades + slides into place rather than
 * snapping.
 */
export function PhaseEnter({ preset, children, fill }: PhaseEnterProps) {
  const style = useEntranceAnimation({ ...preset });

  return (
    <Animated.View style={fill ? [style, { flex: 1, minHeight: 0 }] : style}>
      {children}
    </Animated.View>
  );
}
